import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { createEffect, createEvent, createStore, sample } from "effector";
import { AnalyticsService } from "@features/shared/analytics";
import {
    aiToolsBannerClicked,
    trackUniversalEvent,
    upsellPaywallBuyClicked,
    upsellPaywallFullSkipClicked,
    upsellPaywallPopupClosed,
    upsellPaywallPopupShown,
    upsellPaywallShown,
    upsellPaywallSkipClicked,
    upsellPurchaseCompletedFail,
    upsellRequestError,
    upsellSequenceAssigned,
    UpsellSequenceContext
} from "@features/shared/events";
import { IPersonalBoostPackContract } from "../ui/personal-boost-pack";
import { IAiToolsPackContract } from "../ui/ai-tools-pack";
import { IUltimateAiPackContract } from "../ui/ultimate-ai-pack";
import { BUNDLE_UPSELL_ID, IUpsellContext, IUpsellContract } from ".";
import { ICompleteAiPackContract } from "../ui/complete-ai-pack";
import { IClaudeWorkBundleContract } from "../ui/claude-work-bundle";
import { ICompleteBundleContract } from "../ui/complete-bundle";
import { ICreativePacksContract } from "../ui/creative-packs";
import { IUpsellUIContract } from "../ui";
import { IAppRouteUnitsContract } from "@features/app/route-units";
import { UpsellPlacement } from "@features/shared/entities/subscription";
import { IRouterContract } from "@features/shared/router";
import { IUserAuthContract } from "@features/student/auth/model";

// The screen-level events fire from several places; they funnel through the
// intermediate events below so the sequence context is attached in one spot.
const paywallShownTracked = createEvent<string>();
const buyClickedTracked = createEvent<{ id: string; placement: UpsellPlacement }>();

// $upsellContext speaks the config's language (onboardingId); the events speak
// the analytics one (onboarding).
const toEventContext = (context: IUpsellContext | null): UpsellSequenceContext | null =>
    context ? { onboarding: context.onboardingId, variant: context.variant } : null;

const trackUpsellPaywallShownFx = createEffect(
    ({ id, context }: { id: string; context: UpsellSequenceContext | null }) => {
        upsellPaywallShown(id, context);
    }
);

const trackUpsellSequenceAssignedFx = createEffect(
    (payload: UpsellSequenceContext & { professionUpsells: string; paymentSystem: string; upsells: string[] }) => {
        upsellSequenceAssigned(payload);
    }
);

// Upsell purchases are reported to analytics by the payment webhook, server
// side, where the sequence context is not available — mirroring it onto the
// person profile is what lets those events be attributed. Same reason the
// ai_tools_upsell_variant flag is mirrored in features/amplitude-flags. The
// profession_upsells arm is mirrored for both arms, and only for students in
// the experiment.
const setUpsellSequenceUserPropsFx = createEffect(
    ({ onboarding, variant, professionUpsells }: UpsellSequenceContext & { professionUpsells: string }) => {
        AnalyticsService.setUserProps({
            upsell_onboarding: onboarding,
            upsell_variant: variant,
            ...(professionUpsells ? { profession_upsells: professionUpsells } : {})
        });
    }
);

const trackUpsellPaywallSkipClickedFx = createEffect((id: string) => {
    upsellPaywallSkipClicked(id);
});

const trackUpsellPaywallPopupShownFx = createEffect((id: string) => {
    upsellPaywallPopupShown(id);
});

const trackUpsellPaywallPopupClosedFx = createEffect((id: string) => {
    upsellPaywallPopupClosed(id);
});

// Complete Bundle shows two sequential intro popups; the extra `popup` property
// tells them apart in the shared popup events.
const trackCompleteBundlePopupShownFx = createEffect(({ id, popup }: { id: string; popup: string }) => {
    upsellPaywallPopupShown(id, popup);
});

const trackCompleteBundlePopupClosedFx = createEffect(({ id, popup }: { id: string; popup: string }) => {
    upsellPaywallPopupClosed(id, popup);
});

const trackUpsellPaywallBuyClickedFx = createEffect(
    ({
        id,
        placement,
        context
    }: {
        id: string;
        placement: UpsellPlacement;
        context: UpsellSequenceContext | null;
    }) => {
        upsellPaywallBuyClicked({ id, placement, context });
    }
);

const trackUpsellPaywallFullSkipClickedFx = createEffect((id: string) => {
    upsellPaywallFullSkipClicked(id);
});

const purchaseFailReported = createEvent<{ id: string; paymentId?: string }>();
const $reportedFailurePaymentIds = createStore<string[]>([]);
const trackUpsellPurchaseCompletedFailFx = createEffect(({ id }: { id: string; paymentId?: string }) => {
    upsellPurchaseCompletedFail(id);
});
$reportedFailurePaymentIds.on(trackUpsellPurchaseCompletedFailFx, (reported, { paymentId }) =>
    paymentId ? [...reported, paymentId] : reported
);
sample({
    clock: purchaseFailReported,
    source: $reportedFailurePaymentIds,
    filter: (reported, { paymentId }) => !paymentId || !reported.includes(paymentId),
    fn: (_, failure) => failure,
    target: trackUpsellPurchaseCompletedFailFx
});
const reportPolledFailuresFx = createEffect((failures: Record<string, string[]>) => {
    for (const [id, paymentIds] of Object.entries(failures)) {
        for (const paymentId of paymentIds) purchaseFailReported({ id, paymentId });
    }
});

const trackAiToolsModalOpenedFx = createEffect(() => {
    trackUniversalEvent("upsell_paywall_opened", {
        upsell: "ai_tools"
    });
});

const trackAiToolsModalClosedFx = createEffect(() => {
    trackUniversalEvent("upsell_paywall_closed", {
        upsell: "ai_tools"
    });
});

const trackAiToolsBannerClickedFx = createEffect(
    ({ placement, purchased }: { placement: "profile" | "rail"; purchased: boolean }) => {
        aiToolsBannerClicked({ placement, purchased });
    }
);

const trackUpsellsRequestError = createEffect((error: string) => {
    upsellRequestError(error);
});

const $providerDeviceIdApplied = createStore(false);
const $magicLinkSession = createStore(false);

const contract = new ModelContract({}, {}, {}, {});

export type IUpsellAnalyticsContract = typeof contract;

export class UpsellAnalyticsModule extends ModuleDescription<IUpsellAnalyticsContract> {
    constructor(
        public upsellModule: IUpsellContract,
        public upsellUiModule: IUpsellUIContract,
        public personalBoostPackModule: IPersonalBoostPackContract,
        public aiToolsPackModule: IAiToolsPackContract,
        public ultimateAiPackModule: IUltimateAiPackContract,
        public completeAiPackModule: ICompleteAiPackContract,
        public claudeWorkBundleModule: IClaudeWorkBundleContract,
        public completeBundleModule: ICompleteBundleContract,
        public creativePacksModule: ICreativePacksContract,
        public routesModule: IAppRouteUnitsContract,
        public routerModule: IRouterContract,
        public authModule: IUserAuthContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run() {
        // One join point for the context, so every screen-level event carries
        // the same pair no matter which of the call sites above produced it.
        sample({
            clock: paywallShownTracked,
            source: this.upsellModule.stores.$upsellContext,
            fn: (context, id) => ({ id, context: toEventContext(context) }),
            target: trackUpsellPaywallShownFx
        });

        sample({
            clock: buyClickedTracked,
            source: this.upsellModule.stores.$upsellContext,
            fn: (context, payload) => ({ ...payload, context: toEventContext(context) }),
            target: trackUpsellPaywallBuyClickedFx
        });

        sample({
            clock: this.upsellModule.events.upsellSequenceResolved,
            fn: ({ context, paymentSystem, upsells }) => ({
                onboarding: context.onboardingId,
                variant: context.variant,
                professionUpsells: context.professionUpsells,
                paymentSystem,
                upsells
            }),
            target: trackUpsellSequenceAssignedFx
        });

        // Re-applied on every hydration, not only at assignment: the profile
        // property has to be present for the whole funnel, including sessions
        // that resume a chain resolved earlier.
        sample({
            clock: this.upsellModule.stores.$upsellContext,
            filter: (context: IUpsellContext | null): context is IUpsellContext => context !== null,
            fn: (context: IUpsellContext) => ({
                onboarding: context.onboardingId,
                variant: context.variant,
                professionUpsells: context.professionUpsells
            }),
            target: setUpsellSequenceUserPropsFx
        });

        sample({
            clock: this.upsellModule.stores.$currentUpsellId,
            source: {
                isAssistantRoute: this.routesModule.stores.assistant.$isActive,
                isAiToolsRoute: this.routesModule.stores.aiTools.$isActive,
                isLessonRoute: this.routesModule.stores.lesson.$isActive
            },
            filter: (s, id) => !s.isAssistantRoute && !s.isAiToolsRoute && !s.isLessonRoute && id !== null,
            fn: (_, id) => id!,
            target: paywallShownTracked
        });

        sample({
            clock: this.aiToolsPackModule.events.paywallModalOpened,
            source: { currentUpsellId: this.upsellModule.stores.$currentUpsellId },
            filter: (s) => s.currentUpsellId !== null,
            fn: (s) => s.currentUpsellId!,
            target: trackAiToolsModalOpenedFx
        });

        sample({
            clock: this.aiToolsPackModule.events.paywallModalOpened,
            source: {
                isAssistantRoute: this.routesModule.stores.assistant.$isActive,
                isAiToolsRoute: this.routesModule.stores.aiTools.$isActive,
                isLessonRoute: this.routesModule.stores.lesson.$isActive,
                currentUpsellId: this.upsellModule.stores.$currentUpsellId
            },
            filter: (s) => (s.isAssistantRoute || s.isAiToolsRoute || s.isLessonRoute) && s.currentUpsellId !== null,
            fn: (s) => s.currentUpsellId!,
            target: paywallShownTracked
        });

        sample({
            clock: this.upsellUiModule.events.skipButtonClicked,
            source: { currentUpsellId: this.upsellModule.stores.$currentUpsellId },
            filter: (s) => s.currentUpsellId !== null,
            fn: (s) => s.currentUpsellId!,
            target: trackUpsellPaywallSkipClickedFx
        });

        sample({
            clock: this.upsellUiModule.events.bundleUpsellClosed,
            fn: () => BUNDLE_UPSELL_ID,
            target: trackUpsellPaywallSkipClickedFx
        });

        sample({
            clock: this.upsellUiModule.events.upsellSaleModalOpened,
            source: { currentUpsellId: this.upsellModule.stores.$currentUpsellId },
            filter: (s) => s.currentUpsellId !== null,
            fn: (s) => s.currentUpsellId!,
            target: trackUpsellPaywallPopupShownFx
        });

        sample({
            clock: [
                this.completeAiPackModule.events.modalClosed,
                this.personalBoostPackModule.events.modalClosed,
                this.aiToolsPackModule.events.modalClosed,
                this.ultimateAiPackModule.events.modalClosed,
                this.claudeWorkBundleModule.events.discountModalClosed,
                this.creativePacksModule.events.discountModalClosed
            ],
            source: { currentUpsellId: this.upsellModule.stores.$currentUpsellId },
            filter: (s) => s.currentUpsellId !== null,
            fn: (s) => s.currentUpsellId!,
            target: trackUpsellPaywallPopupClosedFx
        });

        sample({
            clock: [
                this.completeAiPackModule.events.buyButtonClicked,
                this.personalBoostPackModule.events.buyButtonClicked,
                this.aiToolsPackModule.events.buyButtonClicked,
                this.ultimateAiPackModule.events.buyButtonClicked,
                this.claudeWorkBundleModule.events.buyButtonClicked,
                this.completeBundleModule.events.buyButtonClicked,
                this.creativePacksModule.events.buyButtonClicked,
                this.upsellModule.events.upsellUpiPaymentStarted
            ],
            source: {
                currentUpsellId: this.upsellModule.stores.$currentUpsellId,
                firstLoginFlag: this.upsellModule.stores.$firstLoginFlag,
                fromProfile: this.upsellModule.stores.$fromProfile,
                isAssistantRoute: this.routesModule.stores.assistant.$isActive,
                isAiToolsRoute: this.routesModule.stores.aiTools.$isActive,
                isLessonRoute: this.routesModule.stores.lesson.$isActive,
                history: this.routerModule.stores.$history
            },
            filter: (s) => s.currentUpsellId !== null && s.currentUpsellId !== BUNDLE_UPSELL_ID,
            fn: (s): { id: string; placement: UpsellPlacement } => {
                const prevUrl = s.history?.length > 1 ? (s.history.at(-2) ?? "") : "";

                if (s.isAssistantRoute || s.isAiToolsRoute) {
                    return { id: s.currentUpsellId!, placement: UpsellPlacement.Assistant };
                }

                if (s.isLessonRoute) {
                    return { id: s.currentUpsellId!, placement: UpsellPlacement.Lesson };
                }

                if (s.firstLoginFlag) {
                    return { id: s.currentUpsellId!, placement: UpsellPlacement.FirstLogin };
                }

                if ((!prevUrl && !s.fromProfile) || prevUrl.includes("/auth/signin")) {
                    return { id: s.currentUpsellId!, placement: UpsellPlacement.Link };
                }

                if (Boolean(s.fromProfile)) {
                    return { id: s.currentUpsellId!, placement: UpsellPlacement.PromptsLibrary };
                }

                if (s.currentUpsellId === BUNDLE_UPSELL_ID) {
                    return { id: s.currentUpsellId!, placement: UpsellPlacement.Bundle };
                }

                return { id: s.currentUpsellId!, placement: UpsellPlacement.Link };
            },
            target: buyClickedTracked
        });

        sample({
            clock: [this.upsellModule.events.upsellBought, this.upsellModule.events.upsellUpiPaymentStarted],
            source: { currentUpsellId: this.upsellModule.stores.$currentUpsellId },
            filter: (s) => s.currentUpsellId === BUNDLE_UPSELL_ID,
            fn: (s): { id: string; placement: UpsellPlacement } => ({
                id: s.currentUpsellId!,
                placement: UpsellPlacement.Bundle
            }),
            target: buyClickedTracked
        });

        sample({
            clock: this.completeBundleModule.events.giftModalOpened,
            source: { currentUpsellId: this.upsellModule.stores.$currentUpsellId },
            filter: (s) => s.currentUpsellId !== null,
            fn: (s) => ({ id: s.currentUpsellId!, popup: "gift" }),
            target: trackCompleteBundlePopupShownFx
        });

        sample({
            clock: this.completeBundleModule.events.giftModalClosed,
            source: { currentUpsellId: this.upsellModule.stores.$currentUpsellId },
            filter: (s) => s.currentUpsellId !== null,
            fn: (s) => ({ id: s.currentUpsellId!, popup: "gift" }),
            target: trackCompleteBundlePopupClosedFx
        });

        sample({
            clock: this.completeBundleModule.events.discountModalOpened,
            source: { currentUpsellId: this.upsellModule.stores.$currentUpsellId },
            filter: (s) => s.currentUpsellId !== null,
            fn: (s) => ({ id: s.currentUpsellId!, popup: "discount" }),
            target: trackCompleteBundlePopupShownFx
        });

        sample({
            clock: this.completeBundleModule.events.discountModalClosed,
            source: { currentUpsellId: this.upsellModule.stores.$currentUpsellId },
            filter: (s) => s.currentUpsellId !== null,
            fn: (s) => ({ id: s.currentUpsellId!, popup: "discount" }),
            target: trackCompleteBundlePopupClosedFx
        });

        // Every path that ends in the upsell fail modal (see the UI module):
        // HTTP-level charge errors, plus the Solidgate outcomes that arrive as
        // HTTP 200 — a settled decline and a failed / dismissed 3DS challenge.
        sample({
            clock: [
                this.upsellModule.effects.chargeUpsellFx.failData,
                this.upsellModule.effects.registerEcommpayUpsellFx.failData,
                this.upsellModule.effects.registerTruegateUpsellFx.failData,
                this.upsellModule.events.upsell3dsChallengeFailed
            ],
            source: { currentUpsellId: this.upsellModule.stores.$currentUpsellId },
            filter: (s) => s.currentUpsellId !== null,
            fn: (s) => ({ id: s.currentUpsellId! }),
            target: purchaseFailReported
        });

        // The failure names its order when it is not the form's own (an order
        // the page waited on and found declined); the form's order otherwise.
        sample({
            clock: this.upsellModule.events.upsellUpiPaymentFailed,
            source: {
                id: this.upsellModule.stores.$currentUpsellId,
                response: this.upsellModule.stores.$upsellUpiFormResponse
            },
            filter: ({ id }) => id !== null,
            fn: ({ id, response }, failure) => ({
                id: id!,
                paymentId: (failure as { orderId?: string } | undefined)?.orderId ?? response?.order_id
            }),
            target: purchaseFailReported
        });

        sample({
            clock: this.upsellModule.events.upsellChargeDeclined,
            source: this.upsellModule.stores.$currentUpsellId,
            filter: (id) => id !== null,
            fn: (id, response) => ({ id: id!, paymentId: response.order_id }),
            target: purchaseFailReported
        });

        // Solidgate can return "processing" and report the decline after the
        // screen has advanced. Attribute it to the failed product, and dedupe
        // by payment id against synchronous declines and later polling.
        sample({
            clock: this.upsellModule.events.upsellFailedAttemptRegistered,
            fn: ({ newFailedPaymentIds }) => newFailedPaymentIds,
            target: reportPolledFailuresFx
        });

        sample({
            clock: this.personalBoostPackModule.events.skipAndContinueButtonClicked,
            source: { currentUpsellId: this.upsellModule.stores.$currentUpsellId },
            filter: (s) => s.currentUpsellId !== null,
            fn: (s) => s.currentUpsellId!,
            target: trackUpsellPaywallFullSkipClickedFx
        });

        sample({
            clock: this.aiToolsPackModule.events.paywallModalClosed,
            source: { currentUpsellId: this.upsellModule.stores.$currentUpsellId },
            filter: (s) => s.currentUpsellId !== null,
            fn: (s) => s.currentUpsellId!,
            target: trackAiToolsModalClosedFx
        });

        sample({
            clock: this.aiToolsPackModule.events.bannerClicked,
            source: this.aiToolsPackModule.stores.$aiToolsPackStatus,
            fn: (status, { placement }) => ({ placement, purchased: status === "Success" }),
            target: trackAiToolsBannerClickedFx
        });

        sample({
            clock: this.upsellModule.stores.$upsellRequestError,
            filter: (s) => s !== null,
            target: trackUpsellsRequestError
        });

        sample({
            clock: [this.upsellModule.stores.$firstLoginFlag, this.authModule.stores.$student],
            source: {
                firstLoginFlag: this.upsellModule.stores.$firstLoginFlag,
                student: this.authModule.stores.$student,
                applied: $providerDeviceIdApplied
            },
            filter: ({ firstLoginFlag, student, applied }) =>
                firstLoginFlag === true && !applied && !!student?.provider_id,
            fn: ({ student }) => student!.provider_id,
            target: this.authModule.effects.setAmplitudeDeviceIdFromProviderFx
        });

        $magicLinkSession.on(this.authModule.events.magicLinkAuthenticated, () => true);

        sample({
            clock: this.authModule.stores.$student,
            source: {
                magicLinkSession: $magicLinkSession,
                applied: $providerDeviceIdApplied
            },
            filter: ({ magicLinkSession, applied }, student) => magicLinkSession && !applied && !!student?.provider_id,
            fn: (_, student) => student!.provider_id,
            target: this.authModule.effects.setAmplitudeDeviceIdFromProviderFx
        });

        $providerDeviceIdApplied.on(this.authModule.effects.setAmplitudeDeviceIdFromProviderFx.done, () => true);
    }
}
